import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';

import {
  getAttributionName,
  hasSocialFeaturesAccess,
  isAlbumFolderOwner,
  isAlbumOwner,
  isProAccount,
} from '../../../../../../../util/account';
import { getThumbBySize } from '../../../../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import ContributorProfileBadge from '../../../../../../../components/global/ContributorProfileBadge';
import PhotoGridViewerFooterButton from '../../../../../../../components/grid/components/PhotoGridViewerFooterButton';
import DrawerMenu from '../../../../../../../components/global/DrawerMenu';
import FileComments from '../../../../../../../components/global/FileComments';
import CommentIcon from '../../../../../../../components/icons/CommentIcon';
import ReactIcon from '../../../../../../../components/icons/ReactIcon';
import FileReactionPicker from '../../../../../../../components/global/FileReactionPicker';
import useFileReactionPicker from '../../../../../../../components/global/FileReactionPicker/useFileReactionPicker';
import FileReactionList from '../../../../../../../components/global/FileReactionList';
import { PHONE_AUTH_MODAL } from '../../../../../../../constants/modals';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import useShutterbugAccountComplete from '../../../../../hoc/RequireShutterbugAccountSetup/hooks/useShutterbugAccountComplete';
import AccountSetupModal from '../../../../../hoc/RequireShutterbugAccountSetup/components/AccountSetupModal';
import DropdownMenu from '../../../../../../../components/global/DropdownMenu';
import { isAlbum, isProContent } from '../../../../../../../util/album';
import {
  ALBUM_TYPE,
  FILE_COMMENT_PRIVACY,
} from '../../../../../../../constants/albums';
import useFileComments from '../../../../../../../components/global/FileComments/useFileComments';
import PhotoCaption from './components/PhotoCaption';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  entity: PropTypes.shape({
    album_type: PropTypes.oneOf(Object.values(ALBUM_TYPE)),
    display_public_photo_attribution: PropTypes.bool,
  }),
  isFreeWebGallery: PropTypes.bool,
};

const PhotoGridViewerFooter = ({ entity, photo, isFreeWebGallery }) => {
  const location = useLocation();
  const { fileCommentId, parentFileCommentId, reaction } = qs.parse(
    location.search ?? '',
  );

  const openModal = useOpenModal();
  const [commentsOpen, setCommentsOpen] = useState(!!fileCommentId);
  const [accountReactionsOpen, setAccountsReactionsOpen] = useState(!!reaction);
  const isProEntity = isProContent(entity);
  const { accountFileReaction } = useFileReactionPicker({
    photoId: photo?.id,
    isProEntity,
  });
  const { commentsTotal, isOwner } = useFileComments({
    photoId: photo?.id,
    isProEntity,
  });

  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const onAccountCompletedRef = useRef();
  const { user, loading, isAccountSetupComplete } =
    useShutterbugAccountComplete();
  const [formOpen, setFormOpen] = useState(false);
  const blockPro = isProAccount(user) && isFreeWebGallery;

  useEffect(() => {
    if (loading || !authenticated) {
      return;
    }

    if (isAccountSetupComplete) {
      onAccountCompletedRef.current?.();
      // Clear up once called.
      onAccountCompletedRef.current = null;
      setFormOpen(false);
    }
  }, [loading, isAccountSetupComplete, authenticated]);

  if (!photo) {
    return null;
  }

  const displayName = getAttributionName(photo?.attribution);
  const avatarUrl = photo?.attribution?.avatar_photo
    ? getThumbBySize(
        photo.attribution.avatar_photo.thumbnail_urls,
        IMAGE_SIZE_CODE.MEDIUM,
      ).url
    : undefined;
  const contributorTarget = photo.attribution?.username
    ? `/@${photo.attribution.username}`
    : undefined;

  const includeSocialFeatures = hasSocialFeaturesAccess(user) && !isProEntity;

  const displayCommentsAndReactions =
    !blockPro &&
    (isOwner ||
      includeSocialFeatures ||
      (photo?.is_uploader && commentsTotal > 0));

  const isEntityOwner = isAlbum(entity)
    ? isAlbumOwner(user, entity)
    : isAlbumFolderOwner(user, entity);
  const isProEntityOwner = isProAccount(user) && isEntityOwner;

  return (
    <div className="photo-grid-viewer-footer-wrapper">
      <div className="photo-grid-viewer-footer-left">
        <PhotoCaption
          className={
            entity?.display_public_photo_attribution ? 'with-attribution' : null
          }
          photoId={photo?.id}
          isOwner={isOwner}
        />
        {entity?.display_public_photo_attribution && (
          <ContributorProfileBadge
            displayName={displayName}
            displayPhotoSrc={avatarUrl}
            prependText="Photo by:"
            to={contributorTarget}
            className="text-white light"
            accountId={photo?.attribution?.account_id}
            photoAttributionPageEnabled={
              photo?.attribution?.photo_attribution_page?.is_enabled
            }
          />
        )}
      </div>
      <div className="photo-grid-viewer-footer-center" />
      {displayCommentsAndReactions && (
        <Fragment>
          <div className="photo-grid-viewer-footer-right">
            <PhotoGridViewerFooterButton
              label={`Comment (${commentsTotal})`}
              onClick={() => {
                if (!authenticated) {
                  openModal(PHONE_AUTH_MODAL);
                  onAccountCompletedRef.current = () => setCommentsOpen(true);

                  return;
                }

                if (!isAccountSetupComplete) {
                  setFormOpen(true);
                  // Set this method to be called once we confirm the account is completed and good to go.
                  onAccountCompletedRef.current = () => setCommentsOpen(true);

                  return;
                }

                setCommentsOpen(true);
              }}
              icon={<CommentIcon />}
            />
            {includeSocialFeatures && (
              <Fragment>
                {isAccountSetupComplete ? (
                  <DropdownMenu
                    placement="top-end"
                    dropdownClassName="reactions-dropdown"
                    toggle={
                      <PhotoGridViewerFooterButton
                        label="React"
                        onClick={() => {}}
                        icon={
                          accountFileReaction ? (
                            <img
                              src={
                                accountFileReaction?.system_sticker?.asset_image
                                  .url
                              }
                              alt=""
                            />
                          ) : (
                            <ReactIcon />
                          )
                        }
                      />
                    }
                  >
                    <FileReactionPicker photoId={photo.id} />
                  </DropdownMenu>
                ) : (
                  <PhotoGridViewerFooterButton
                    label="React"
                    onClick={() => {
                      setFormOpen(true);
                    }}
                    icon={<ReactIcon />}
                  />
                )}
              </Fragment>
            )}
          </div>
          <DrawerMenu
            title="Comments"
            isOpen={commentsOpen}
            onClose={() => setCommentsOpen(false)}
          >
            <FileComments
              photoId={photo.id}
              onGroupedReactionsClick={() => {
                if (!authenticated) {
                  openModal(PHONE_AUTH_MODAL);
                  onAccountCompletedRef.current = () =>
                    setAccountsReactionsOpen(true);

                  return;
                }

                setAccountsReactionsOpen(true);
                setCommentsOpen(false);
              }}
              target={{ fileCommentId, parentFileCommentId }}
              privateCommentingEnabled={
                (entity?.file_comment_privacy ||
                  entity?.root_album_folder?.file_comment_privacy) !==
                FILE_COMMENT_PRIVACY.PUBLIC
              }
              forceRepliesOpen={isProEntityOwner}
            />
          </DrawerMenu>
          {includeSocialFeatures && (
            <DrawerMenu
              title="Reactions"
              isOpen={accountReactionsOpen}
              onClose={() => {
                setAccountsReactionsOpen(false);
                setCommentsOpen(true);
              }}
            >
              <FileReactionList photoId={photo.id} />
            </DrawerMenu>
          )}
        </Fragment>
      )}
      <AccountSetupModal
        user={user}
        forceOpen={formOpen}
        onAccountSetupComplete={() => {
          setFormOpen(false);
        }}
        onCancelAccountSetup={() => {
          onAccountCompletedRef.current = null;
          setFormOpen(false);
        }}
      />
    </div>
  );
};

PhotoGridViewerFooter.propTypes = propTypes;

export default PhotoGridViewerFooter;
